import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';

import { OVERLAY_DIMENSIONS } from '../../constants/Overlay';
import { ScrollKeys } from '../../constants/Scroll';
import {
  addDocumentEventListener,
  clearTimeout,
  removeDocumentEventListener,
  setTimeout,
} from '../../tools/Globals';

import './OverlayAnchor.scss';

/*
Terminology:
  Target: this.props.children / the element you will wrap with <OverlayAnchor>
    Its location will be used to position the overlay
    If you don't disable doesHaveArrow this is what the MiniCoachmark arrow will point to

  Overlay: this.props.overlay / your MiniCoachmark or, someday, Dropdown

Example:

<OverlayAnchor overlay={(<MyComponent {...myProps}/>)} />
  <div>
    This div is used to position the overlay.
  </div>
</OverlayAnchor>
*/

class OverlayAnchor extends React.Component {
  static propTypes = {
    overlay: PropTypes.shape({
      id: PropTypes.string.isRequired,
      // overlay: React element e.g. (<div>I'm a overlay</div>) or (<MiniCoachmark/>) or a function that returns an element
      overlay: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
      // xOffset: push the overlay's origin in the x direction. Useful when center of element is not visually centered
      xOffset: PropTypes.number,
      // yOffset: push the overlay's origin in the x direction. Useful when the top of the element has significant padding
      yOffset: PropTypes.number,
      // targetOrigin: where the overlay will be positioned relative to the target
      targetOrigin: PropTypes.oneOf(['top', 'bottom']), // likely adding support for 'right' and 'left' later
      // doesHaveArrow: display an arrow for that talk bubble effect
      doesHaveArrow: PropTypes.bool,
      // timeout: amount of time in milliseconds the overlay will be open before being automatically closed
      timeout: PropTypes.number,
    }),
    // children: this will be the positioning element that we are wrapping with overlay logic
    children: PropTypes.element,
    // onClose: function that handles the close event
    onClose: PropTypes.func,
    // closeOnExternalClick - default true: if true, overlay will close when user clicks outside of it
    closeOnExternalClick: PropTypes.bool,
    // shouldScroll - default false: if true, overlay will scroll with the page, fixed otherwise
    shouldScroll: PropTypes.bool,
    scrolledOffset: PropTypes.number,
  };

  static defaultProps = {
    onClose: () => {},
    closeOnExternalClick: true,
  };

  state = {
    arrowStyle: null,
    bodyStyle: null,
  };

  shouldComponentUpdate(nextProps, nextState) {
    if (!isEqual(this.state, nextState)) {
      return true;
    }
    if (!isEqual(this.props.overlay, nextProps.overlay)) {
      return true;
    }
    if (!isEqual(this.props.children, nextProps.children)) {
      return true;
    }
    if (!nextProps.shouldScroll) {
      return false;
    }
    return this.props.scrolledOffset !== nextProps.scrolledOffset;
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.overlay, prevProps.overlay)) {
      if (this.props.overlay) {
        this.calculateStyles(this.props.overlay);
        this.setCloseHooks(this.props.overlay);
      } else {
        this.clearCloseHooks();
      }
    }
  }

  componentWillUnmount() {
    this.onClose();
  }

  timeoutId = null;
  overlayHeight = 0;

  // overlayWidth/Height can't be in state because we need the new values immediately after and setState
  // is async
  overlayWidth = 0;

  getConfigDefaults = () => {
    return {
      xOffset: 0,
      yOffset: 0,
      targetOrigin: 'top',
      doesHaveArrow: true,
      timeout: null,
    };
  };

  setCloseHooks = config => {
    if (this.props.closeOnExternalClick) {
      addDocumentEventListener('click', this.onDocumentClick);
    }
    this.setTimeout(config);
  };

  clearCloseHooks = () => {
    removeDocumentEventListener('click', this.onDocumentClick);
    this.clearTimeout();
  };

  setTimeout = config => {
    this.clearTimeout();
    if (config.timeout) {
      this.timeoutId = setTimeout(() => {
        this.onClose();
      }, config.timeout);
    }
  };

  clearTimeout = () => {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  };

  onClose = () => {
    this.clearCloseHooks();
    this.props.onClose();
  };

  onOverlayClick = () => {
    if (this.props.closeOnExternalClick) {
      this.setState({ cancelDocumentClick: true });
    }
  };

  onDocumentClick = () => {
    if (this.state.cancelDocumentClick) {
      this.setState({ cancelDocumentClick: false });
      return;
    }
    this.onClose();
  };

  getOverlay = config => {
    const { overlay } = config;
    return isFunction(overlay) ? overlay() : overlay;
  };

  getOverlayDimensions = overlay => {
    if (!overlay) {
      return;
    }
    const overlayBounds = overlay.getBoundingClientRect();
    if (overlayBounds.width !== this.overlayWidth || overlayBounds.height !== this.overlayHeight) {
      this.overlayWidth = overlayBounds.width;
      this.overlayHeight = overlayBounds.height;
    }
  };

  getTargetBounds = () => {
    // eslint-disable-next-line react/no-find-dom-node
    const target = ReactDOM.findDOMNode(this.refs.wrapper);
    const targetBounds = target.getBoundingClientRect();
    return {
      top: targetBounds.top,
      bottom: targetBounds.bottom,
      left: targetBounds.left,
      right: targetBounds.right,
      width: targetBounds.width,
      height: targetBounds.height,
      centerY: targetBounds.top + targetBounds.height / 2,
      centerX: targetBounds.left + targetBounds.width / 2,
    };
  };

  calculateStyles = _config => {
    const config = Object.assign(this.getConfigDefaults(), _config);
    const targetBounds = this.getTargetBounds();
    const xTarget = targetBounds.centerX + config.xOffset;
    const targetToOverlayDirection = config.targetOrigin;

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const overlayWidth = this.overlayWidth || OVERLAY_DIMENSIONS.DEFAULT_WIDTH;
    const overlayPadding = config.doesHaveArrow ? OVERLAY_DIMENSIONS.ARROW_HEIGHT : 0;

    const arrowStyle = {};
    const bodyStyle = {};
    let arrowCssClass;
    const offsetFromCenterOfWindow = windowWidth / 2 - xTarget;
    if (
      xTarget + overlayPadding + overlayWidth / 2 > windowWidth ||
      xTarget - overlayWidth / 2 - overlayPadding < 0
    ) {
      // would otherwise overflow out of window
      if (offsetFromCenterOfWindow < 0) {
        // would otherwise overflow off right
        bodyStyle.right = Math.min(overlayPadding, windowWidth - (xTarget - overlayWidth / 2));
      } else {
        // would otherwise overflow off left
        bodyStyle.left = Math.max(overlayPadding, xTarget - overlayWidth / 2);
      }
    } else {
      bodyStyle.left = xTarget - overlayWidth / 2;
    }
    arrowStyle.left = xTarget;
    if (targetToOverlayDirection === 'top') {
      arrowStyle.bottom = windowHeight - targetBounds.top + config.yOffset;
      bodyStyle.bottom = arrowStyle.bottom + overlayPadding;
    } else {
      // bottom
      arrowStyle.top = targetBounds.bottom + config.yOffset;
      bodyStyle.top = arrowStyle.top + overlayPadding;
      arrowCssClass = 'OverlayWrapper__OverlayContainer__OverlayArrow--bottom';
    }

    this.setState({ bodyStyle, arrowStyle, arrowCssClass });
  };

  getOverlayJSX = config => {
    if (!config) {
      return null;
    }
    const arrowClassNames = classNames([
      'OverlayWrapper__OverlayContainer__OverlayArrow',
      {
        [this.state.arrowCssClass]: this.state.arrowCssClass,
      },
    ]);
    const wrapperClassNames = classNames([
      'OverlayWrapper__OverlayContainer',
      {
        'OverlayWrapper__OverlayContainer--scrollable': this.props.shouldScroll,
      },
    ]);
    return ReactDOM.createPortal(
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div className={wrapperClassNames} onClick={this.onOverlayClick}>
        <div
          style={this.state.bodyStyle}
          className='OverlayWrapper__OverlayContainer__OverlayBody'
          ref={this.getOverlayDimensions}
        >
          {this.getOverlay(config)}
        </div>
        <div className={arrowClassNames} style={this.state.arrowStyle} />
      </div>,
      window.document.body,
    );
  };

  render() {
    const config = this.props.overlay;

    return (
      <span className='OverlayWrapper' ref='wrapper'>
        {React.Children.map(this.props.children, element => {
          return React.cloneElement(element);
        })}
        {this.refs.wrapper && this.getOverlayJSX(config)}
      </span>
    );
  }
}

export function mapStateToProps(state) {
  return {
    scrolledOffset: state.scroll.get(ScrollKeys.SCROLLED_OFFSET),
  };
}

export default connect(mapStateToProps)(OverlayAnchor);
